import {type MatchParams} from '@wandb/weave/common/types/base';
import {useBooleanState} from '@wandb/weave/common/util/hooks';
import {type SetState} from '@wandb/weave/common/util/types';
import _ from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {useLocation} from 'react-router-dom';

import {useSearchNavQueryQuery} from '../generated/graphql';
import {useCurrentRouteMatchParams} from '../routes/utils';
import {registerGlobalSearchOpener} from '../shellRemote';
import {isInIframe} from '../util/window';
import type {SearchNavQueryData} from './Search';

/**
 * Router location state that a page can set on a `history.replace` when it
 * rewrites its own URL (e.g. selecting a subtab) so the global nav is not
 * reset as if the page had changed.
 */
export type NavLocationState = {
  preserveGlobalNav?: boolean;
};

// TODO(forge): Legacy global nav state. Only renders in the non-Forge (standalone) entry point; dedicated deployments still use it. Remove entirely once dedicated is switched over to Forge shell mode. In shell mode the home page renders ForgeHomeSidebar instead.
type GlobalNavState = {
  /**
   * The global nav sidebar is either "inline" or "floating",
   * depending on the page. An inline sidebar flows with the
   * rest of the page contents, while a floating sidebar sits
   * on top of the page contents and covers them.
   */
  isInline: boolean;
  /**
   * Controls whether the global nav sidebar is shown
   */
  isOpen: boolean;
  /**
   * If nav is inline, a shadow is applied to the sidebar
   * when the toggle button is hovered.
   */
  isTriggerHovered: boolean;
  /**
   * Controls whether animation will occur on open and close.
   * It's a bit hacky, but the reason for this is explained here:
   * https://github.com/wandb/core/pull/18117#discussion_r1395020152
   */
  shouldAnimate: boolean;
};

type NavContextState = {
  params: MatchParams;
  hidden: boolean;
  shouldShowBreadcrumbs: boolean;
  searchLoading: boolean;
  searchData: SearchNavQueryData | undefined;
  searchActive: boolean;
  globalNav: GlobalNavState;
  isCreatingTeam: boolean;
  /**
   * Whether CreateProjectDrawer is open from global nav sidebar specifically.
   * Not shared with other CreateProjectDrawer instances across the app.
   */
  isCreatingProject: boolean;
};

type NavUpdaterContextState = {
  setHidden: SetState<boolean>;
  setShouldShowBreadcrumbs: SetState<boolean>;
  toggleSearch: () => void;
  activateSearch: () => void;
  deactivateSearch: () => void;
  updateGlobalNav: (changedValues: Partial<GlobalNavState>) => void;
  toggleTeamDrawer: () => void;
  toggleCreateProjectDrawer: () => void;
};

export const defaultNavContext: NavContextState = {
  params: {},
  hidden: false,
  shouldShowBreadcrumbs: true,
  searchLoading: false,
  searchData: undefined,
  searchActive: false,
  globalNav: {
    isInline: false,
    isOpen: false,
    isTriggerHovered: false,
    shouldAnimate: true,
  },
  isCreatingTeam: false,
  isCreatingProject: false,
};
const NavContext = createContext<NavContextState>(defaultNavContext);
NavContext.displayName = 'NavContext';

const NavUpdaterContext = createContext<NavUpdaterContextState>({
  setHidden: () => {},
  setShouldShowBreadcrumbs: () => {},
  toggleSearch: () => {},
  activateSearch: () => {},
  deactivateSearch: () => {},
  updateGlobalNav: () => {},
  toggleTeamDrawer: () => {},
  toggleCreateProjectDrawer: () => {},
});
NavUpdaterContext.displayName = 'NavUpdaterContext';

export function useNavContext(): NavContextState {
  return useContext(NavContext);
}
export function useNavUpdaterContext(): NavUpdaterContextState {
  return useContext(NavUpdaterContext);
}

type NavContextProviderProps = {
  children?: React.ReactNode;
};

const NavContextProvider: React.FC<NavContextProviderProps> = ({children}) => {
  const [hidden, setHidden] = useState(false);
  const [shouldShowBreadcrumbs, setShouldShowBreadcrumbs] = useState(true);
  const {
    state: searchActive,
    setTrue: activateSearch,
    setFalse: deactivateSearch,
    toggle: toggleSearch,
  } = useBooleanState(false);
  // The Forge shell's header search button lives outside this React root.
  useEffect(() => registerGlobalSearchOpener(activateSearch), [activateSearch]);

  const [globalNav, setGlobalNav] = useState<GlobalNavState>({
    isInline: false,
    isOpen: false,
    isTriggerHovered: false,
    shouldAnimate: true,
  });
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const toggleTeamDrawer = () => {
    setIsCreatingTeam(!isCreatingTeam);
  };
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const toggleCreateProjectDrawer = () => {
    setIsCreatingProject(prev => !prev);
  };
  const updateGlobalNav = useCallback(
    (changedValues: Partial<GlobalNavState>) => {
      setGlobalNav(prevValues => {
        const mergedValues = {...prevValues, ...changedValues};
        // must check if values actually changed to prevent unwanted re-renders
        const isUnchanged = _.isEqual(prevValues, mergedValues);
        return isUnchanged ? prevValues : mergedValues;
      });
    },
    []
  );

  // Reset global nav on location change. By default, inline nav
  // should be open and floating nav should be closed.
  const location = useLocation<NavLocationState | undefined>();
  const {pathname} = location;
  const preserveGlobalNav = location.state?.preserveGlobalNav ?? false;
  useEffect(() => {
    if (preserveGlobalNav) {
      return;
    }
    updateGlobalNav({
      isOpen: globalNav.isInline,
      shouldAnimate: false,
    });
  }, [pathname, preserveGlobalNav, globalNav.isInline, updateGlobalNav]);
  const params = useCurrentRouteMatchParams();
  const {loading: searchLoading, data: searchNavData} = useSearchNavQueryQuery({
    variables: {
      entityName: params.entityName ?? '',
      projectName: params.projectName ?? '',
      runName: params.runName ?? '',
      sweepName: params.sweepName ?? '',
    },
    skip: hidden || isInIframe(),
  });
  const searchData = searchNavData as SearchNavQueryData | undefined;

  const value = {
    params,
    hidden,
    shouldShowBreadcrumbs,
    searchLoading,
    searchData,
    searchActive,
    globalNav,
    isCreatingTeam,
    isCreatingProject,
  };

  const updaterValue = {
    setHidden,
    setShouldShowBreadcrumbs,
    toggleSearch,
    activateSearch,
    deactivateSearch,
    updateGlobalNav,
    toggleTeamDrawer,
    toggleCreateProjectDrawer,
  };

  // React Compiler caches both context values; this component must stay
  // compiled for that to hold.
  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values
    <NavContext.Provider value={value}>
      {/* oxlint-disable-next-line react/jsx-no-constructed-context-values */}
      <NavUpdaterContext.Provider value={updaterValue}>
        {children}
      </NavUpdaterContext.Provider>
    </NavContext.Provider>
  );
};
NavContextProvider.displayName = 'NavContextProvider';

export function useHideNavOnPage(): void {
  const {setHidden} = useNavUpdaterContext();
  useEffect(() => {
    setHidden(true);
    return () => setHidden(false);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default NavContextProvider;
